import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { replace, goBack } from 'connected-react-router';

import {
  fetchSlot,
  cancelSlot,
} from 'data_system/resource_portal/actions/slotsActions';
import {
  getSlot,
  getLocation,
  isResourceCreateExperienceEnabled,
  isResourceCancelBookingsEnabled,
  getPartnerTranslations,
} from 'data_system/resource_portal/selectors';

import { Theme } from 'ui_system/theme';

import { translateWithObject } from 'services/translation';
import { SLOT_LIST } from '../routes';

import NavigationBar from '../../components/NavigationBar';
import SlotHeader from './components/SlotHeader';
import BookingRow from './components/BookingRow';
import BookingModal from './components/BookingModal';
import CancelSlotModal, { CancelButton } from './components/CancelSlotModal';

const Container = styled('div')``;
const BookingsContainer = styled('div')``;
const BookingsHeader = styled('div')`
  padding-left: 15px;
  padding-right: 15px;
  padding-top: 5px;
  padding-bottom: 5px;
  background-color: ${Theme.colors.lightBackground};
`;

const NoData = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  color: ${Theme.colors.lightText};
  border-bottom: 1px solid ${Theme.colors.lightLine};
`;

const SlotDetails = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [cancelSlotModalOpen, setCancelSlotModalOpen] = useState(false);

  const location = useSelector(getLocation);
  const cancellationAllowed = useSelector(isResourceCreateExperienceEnabled);
  const bookingCancellationAllowed = useSelector(
    isResourceCancelBookingsEnabled
  );
  const slotFetching = useSelector((state) => state.slots.slotFetching);
  const slotCancelling = useSelector((state) => state.slots.slotCancelling);
  const slot = useSelector(getSlot);
  const isLoading = slotFetching || !slot.attributes;
  const partnerTranslations = useSelector(getPartnerTranslations);

  const t = translateWithObject(partnerTranslations);

  useEffect(() => {
    dispatch(fetchSlot(id));
  }, [id]);

  let bookings = [];
  if (!isLoading) bookings = slot.attributes.bookings.data;
  const selectedBooking = bookings.find(
    (booking) => booking.id === selectedBookingId
  );

  const renderCancellationComponents = () => {
    if (isLoading || !cancellationAllowed) return null;

    const { cancellableBookingsCount, inPast } = slot.attributes;
    if (inPast) return null;
    if (!bookingCancellationAllowed && cancellableBookingsCount > 0)
      return null;

    return (
      <React.Fragment>
        <CancelButton
          inverted
          text={t(`CANCEL ${t('EXPERIENCE_NAME_SINGULAR')}`)}
          onClick={() => setCancelSlotModalOpen(true)}
        />
        <CancelSlotModal
          open={cancelSlotModalOpen}
          slot={slot.attributes}
          close={() => setCancelSlotModalOpen(false)}
          onCancel={(guestNote) =>
            dispatch(cancelSlot({ slotId: id, guestNote }))
          }
          cancelling={slotCancelling}
          hasBookings={cancellableBookingsCount > 0}
          t={t}
        />
      </React.Fragment>
    );
  };

  const renderBookings = () => {
    if (isLoading) return null;

    return (
      <BookingsContainer>
        <BookingsHeader>{t('CONFIRMED')}</BookingsHeader>
        {bookings && bookings.length > 0 ? (
          bookings.map((booking) => (
            <BookingRow
              {...booking.attributes}
              id={booking.id}
              key={booking.id}
              onSelect={(bookingId) => {
                setSelectedBookingId(bookingId);
              }}
            />
          ))
        ) : (
          <NoData>{t('NO_CONFIRMED_BOOKINGS_YET')}</NoData>
        )}
      </BookingsContainer>
    );
  };

  const onBack = () => {
    const { state } = location;

    // We use the browser's goBack to preserve the state of the SlotList if we came from there
    if (state && state.navigatedFromList) {
      dispatch(goBack());
    } else {
      dispatch(replace(SLOT_LIST));
    }
  };

  return (
    <Container>
      <NavigationBar
        backText={`${t('ALL_SCHEDULED_CLASSES')}`}
        onBack={onBack}
      />
      <SlotHeader
        slot={slot.attributes}
        loading={isLoading}
        t={t}
        enableLaunchClassButton
      />
      {renderBookings()}
      {renderCancellationComponents()}
      <BookingModal
        open={!!selectedBookingId}
        booking={selectedBooking}
        close={() => setSelectedBookingId(null)}
      />
    </Container>
  );
};

export default SlotDetails;
